import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { toast } from "sonner";
import { Sparkles, LogOut, Mail, Globe, KeyRound, Trash2, Lock, BellOff } from "lucide-react";
import PinPad from "../components/PinPad";
import Brand from "../components/Brand";
import {
  usePasskeysAvailable,
  useMyPasskeys,
  registerPasskey,
} from "../lib/passkeys";
import { toast as sonnerToast } from "sonner";
import { useI18n } from "../lib/i18n";

const REGIONS = [
  { value: "GLOBAL", label: "Global" },
  { value: "EU", label: "Europe" },
  { value: "US", label: "Americas" },
  { value: "APAC", label: "Asia-Pacific" },
];

export default function Settings() {
  const { user, signOut, setUser } = useAuth();
  const navigate = useNavigate();
  const [optIn, setOptIn] = useState(user?.digest_email_opt_in || false);
  const [region, setRegion] = useState(user?.region || "GLOBAL");
  const [hour, setHour] = useState(user?.digest_local_hour ?? 19);
  const [saving, setSaving] = useState(false);
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);

  useEffect(() => {
    setOptIn(user?.digest_email_opt_in || false);
    setRegion(user?.region || "GLOBAL");
    setHour(user?.digest_local_hour ?? 19);
  }, [user]);

  useEffect(() => {
    api
      .get("/users/me/email-status")
      .then((r) => setEmailConfigured(r.data.email_configured))
      .catch(() => {});
  }, []);

  const patchPrefs = async (payload) => {
    setSaving(true);
    try {
      const r = await api.patch("/users/me", payload);
      setUser(r.data);
      toast("Saved. Quietly.");
      return r.data;
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't save just now.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const toggleOptIn = () => {
    const next = !optIn;
    setOptIn(next);
    patchPrefs({ digest_email_opt_in: next });
  };

  const changeRegion = (r) => {
    setRegion(r);
    patchPrefs({ region: r });
  };

  const changeHour = (h) => {
    setHour(h);
    patchPrefs({
      digest_local_hour: h,
      timezone_offset_minutes: -new Date().getTimezoneOffset(),
    });
  };

  const preview = async () => {
    setBusy(true);
    try {
      const r = await api.get("/users/me/digest");
      setDigest(r.data);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't prepare a preview right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Brand size="sm" dataTestId="brand-settings" />
          <button
            data-testid="settings-signout"
            onClick={() => {
              signOut();
              navigate("/");
            }}
            className="text-[#6A6863] hover:text-[#2C2B29] inline-flex items-center gap-2 text-sm transition-colors"
          >
            Sign out
            <LogOut size={14} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-8 pb-12 fade-up">
        <span className="eyebrow">Preferences</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          Held the way{" "}
          <em className="italic text-[#8A9984]">you would like.</em>
        </h1>

        <section className="mt-12 rounded-3xl border hairline bg-white p-6 sm:p-9">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Mail size={14} strokeWidth={1.5} className="text-[#8A9984]" />
                <span className="eyebrow">A daily breath</span>
              </div>
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
                One calm paragraph, once a day.
              </h2>
              <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
                We&rsquo;ll quietly weave today&rsquo;s moments and send a single,
                unhurried paragraph by email. Never more than once a day. Never an alert.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleOptIn}
              disabled={saving}
              data-testid="settings-digest-toggle"
              aria-pressed={optIn}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-500 shrink-0 ${
                optIn ? "bg-[#9BA995]" : "bg-[#2C2B29]/15"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-500 ${
                  optIn ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="mt-8 pt-6 border-t hairline">
            <p className="text-xs text-[#6A6863] tracking-wide">
              {emailConfigured
                ? "Email delivery is connected. We'll send at your local hour, once per day."
                : "Email delivery isn't plugged in yet. The wiring is ready — Alouvya just needs a sender to send through."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={preview}
                disabled={busy}
                data-testid="settings-digest-preview"
                className="btn-ghost"
              >
                {busy ? "Listening…" : "Preview today's digest"}
                <Sparkles size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t hairline">
            <span className="eyebrow">Send at my local hour</span>
            <div className="mt-4 flex flex-wrap gap-2">
              {[7, 12, 17, 19, 21].map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => changeHour(h)}
                  disabled={saving}
                  data-testid={`settings-hour-${h}`}
                  className={`rounded-full px-4 py-2 text-xs tracking-widest uppercase transition-colors duration-300 border ${
                    hour === h
                      ? "bg-[#E6BFA6] text-[#2C2B29] border-[#E6BFA6]"
                      : "bg-transparent text-[#6A6863] border-[#2C2B29]/10 hover:bg-[#FBF9F6]"
                  } ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {h}:00
                </button>
              ))}
            </div>
          </div>
        </section>

        {digest && digest.items.length > 0 && (
          <section
            data-testid="settings-digest-preview-result"
            className="mt-10 rounded-3xl border hairline bg-[#F2EFEA]/60 p-7 sm:p-9 space-y-8"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#8A9984]" />
              <span className="eyebrow">Today, in your inbox would say</span>
            </div>
            {digest.items.map((item, i) => (
              <div
                key={item.child_id}
                data-testid={`digest-item-${i}`}
                className="border-l hairline pl-5"
              >
                <div className="eyebrow">{item.child_name}</div>
                <p className="mt-3 font-serif text-xl sm:text-2xl italic text-[#2C2B29] leading-snug">
                  {item.summary}
                </p>
              </div>
            ))}
          </section>
        )}

        <section className="mt-16" data-testid="settings-region">
          <div className="flex items-center gap-2">
            <Globe size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">Region preference</span>
          </div>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
            Where would you like to be held?
          </h2>
          <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
            A signal of where your data sovereignty lives. Today this is visible. As Alouvya
            expands its regional infrastructure, your data will be held in your chosen
            region by default.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {REGIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => changeRegion(r.value)}
                disabled={saving}
                data-testid={`settings-region-${r.value}`}
                className={`rounded-full px-5 py-2.5 text-xs tracking-widest uppercase transition-colors duration-300 border ${
                  region === r.value
                    ? "bg-[#E6BFA6] text-[#2C2B29] border-[#E6BFA6]"
                    : "bg-transparent text-[#6A6863] border-[#2C2B29]/10 hover:bg-[#FBF9F6]"
                } ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="settings-passkeys-section">
          <PasskeysPanel />
        </section>

        <section className="mt-16" data-testid="settings-kiosk-pin-section">
          <KioskPinSection />
        </section>

        <section className="mt-16" data-testid="settings-kiosk-approval-section">
          <KioskApprovalSection />
        </section>

        <section className="mt-16" data-testid="settings-notifications-section">
          <NotificationsTonePanel />
        </section>

        <section className="mt-16" data-testid="settings-language-section">
          <LanguagePanel />
        </section>

        <section className="mt-16">
          <span className="eyebrow">You</span>
          <div className="mt-5 rounded-3xl border hairline bg-white p-6 sm:p-8">
            <div className="font-serif text-2xl">{user?.name}</div>
            <div className="mt-1 text-sm text-[#6A6863]">{user?.email}</div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#8A9984]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#9BA995]" />
              {user?.role}
              <span className="text-[#6A6863]/40">·</span>
              <span className="text-[#6A6863]">
                {user?.public_key_jwk ? "encrypted device" : "no device key"}
              </span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function PasskeysPanel() {
  const available = usePasskeysAvailable();
  const { list, refresh } = useMyPasskeys();
  const [busy, setBusy] = useState(false);

  const register = async () => {
    setBusy(true);
    try {
      await registerPasskey("this device");
      await refresh();
      sonnerToast("A passkey, gently saved on this device.");
    } catch {
      // toast already shown by helper
    } finally {
      setBusy(false);
    }
  };

  const remove = async (credId) => {
    try {
      await api.delete(`/passkeys/${encodeURIComponent(credId)}`);
      await refresh();
      sonnerToast("Passkey removed.");
    } catch (err) {
      sonnerToast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  if (!available) {
    return (
      <>
        <div className="flex items-center gap-2">
          <KeyRound size={14} strokeWidth={1.5} className="text-[#8A9984]" />
          <span className="eyebrow">Biometric sign-in</span>
        </div>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
          Not available on this device.
        </h2>
        <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
          Passkeys need a device with Touch ID, Face ID, Windows Hello, or a security key.
        </p>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <KeyRound size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Biometric sign-in</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        Sign in with a calm touch.
      </h2>
      <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
        Add a passkey on this device and sign in with Touch ID, Face ID, or your screen
        unlock — no password needed. Stored on the device, never on our servers.
      </p>
      <div className="mt-7">
        <button
          type="button"
          onClick={register}
          disabled={busy}
          data-testid="settings-add-passkey"
          className="btn-primary"
        >
          {busy ? "Sealing…" : "Add a passkey on this device"}
          <KeyRound size={14} strokeWidth={1.5} />
        </button>
      </div>
      {list.length > 0 && (
        <ul
          data-testid="settings-passkeys-list"
          className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
        >
          {list.map((p) => (
            <li
              key={p.id}
              data-testid={`settings-passkey-${p.id}`}
              className="bg-[#FBF9F6] p-6 flex items-center justify-between gap-4"
            >
              <div>
                <div className="font-serif text-lg">{p.nickname || "this device"}</div>
                <div className="text-xs text-[#6A6863] mt-1">
                  added {new Date(p.created_at).toLocaleDateString()}
                  {p.last_used_at
                    ? ` · last used ${new Date(p.last_used_at).toLocaleDateString()}`
                    : " · never used"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(p.credential_id_b64)}
                data-testid={`settings-passkey-remove-${p.id}`}
                aria-label="Remove"
                className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
              >
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}


function KioskPinSection() {
  const { user } = useAuth();
  const { list, refresh } = useChild();
  const [activeChild, setActiveChild] = useState(null); // child object opened in the pad
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Only children where this user is a parent, the assigned caregiver, or implicitly admin.
  const visible = (list || []).filter((c) => {
    if (!user) return false;
    if (c.parent_ids?.includes(user.id)) return true;
    if (c.caregiver_id === user.id) return true;
    return false;
  });

  const openSet = (child) => {
    setError(null);
    setActiveChild(child);
  };

  const closePad = () => {
    setActiveChild(null);
    setError(null);
  };

  const handleSet = async (pin) => {
    if (!activeChild) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/children/${activeChild.id}/kiosk-pin`, { pin });
      await refresh();
      sonnerToast("Kiosk PIN saved. Gently.");
      setActiveChild(null);
    } catch (err) {
      setError(
        err.response?.data?.detail || "Couldn't save the PIN. Try again, gently."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async (child) => {
    try {
      await api.delete(`/children/${child.id}/kiosk-pin`);
      await refresh();
      sonnerToast("Kiosk PIN removed.");
    } catch (err) {
      sonnerToast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  if (visible.length === 0) {
    return (
      <>
        <div className="flex items-center gap-2">
          <Lock size={14} strokeWidth={1.5} className="text-[#8A9984]" />
          <span className="eyebrow">Kiosk PIN</span>
        </div>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
          Nothing to lock just yet.
        </h2>
        <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
          Once a child is connected, you can set a four-digit PIN to keep their
          kiosk gently undisturbed.
        </p>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Lock size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Kiosk PIN</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        A quiet lock on the kiosk.
      </h2>
      <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
        Anyone can tap and share a feeling. Only someone who knows the four-digit
        PIN can leave kiosk mode. Stored hashed — never visible, never recovered.
      </p>

      <ul
        data-testid="settings-kiosk-pin-list"
        className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {visible.map((c) => (
          <li
            key={c.id}
            data-testid={`settings-kiosk-pin-row-${c.id}`}
            className="bg-[#FBF9F6] p-6 flex items-center justify-between gap-4 flex-wrap"
          >
            <div>
              <div className="font-serif text-lg">{c.name}</div>
              <div
                className="text-xs text-[#6A6863] mt-1"
                data-testid={`settings-kiosk-pin-status-${c.id}`}
              >
                {c.kiosk_pin_set ? "PIN is set" : "No PIN — exit is open"}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => openSet(c)}
                data-testid={`settings-kiosk-pin-set-${c.id}`}
                className="btn-ghost !py-2 !px-4 !text-xs"
              >
                {c.kiosk_pin_set ? "Change PIN" : "Set PIN"}
                <Lock size={12} strokeWidth={1.5} />
              </button>
              {c.kiosk_pin_set && (
                <button
                  type="button"
                  onClick={() => handleClear(c)}
                  data-testid={`settings-kiosk-pin-clear-${c.id}`}
                  aria-label="Remove PIN"
                  className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {activeChild && (
        <div
          data-testid="settings-kiosk-pin-overlay"
          className="fixed inset-0 z-[70] bg-[#FBF9F6]/95 backdrop-blur-xl flex items-center justify-center px-6"
        >
          <div className="relative w-full max-w-sm bg-white rounded-3xl border hairline p-10 fade-up">
            <button
              type="button"
              onClick={closePad}
              data-testid="settings-kiosk-pin-cancel"
              aria-label="Cancel"
              className="absolute top-4 right-4 text-[#6A6863] hover:text-[#2C2B29] p-2 transition-colors"
            >
              ×
            </button>
            <PinPad
              busy={busy}
              error={error}
              onComplete={handleSet}
              label={
                activeChild.kiosk_pin_set
                  ? `New PIN for ${activeChild.name}.`
                  : `Choose a PIN for ${activeChild.name}.`
              }
              subtitle="Tap four digits. We'll keep them gently hashed."
            />
          </div>
        </div>
      )}
    </>
  );
}


const TONES = [
  {
    id: "minimal",
    label: "Minimal",
    blurb: "Only arrivals, departures, and a Circle of Calm. Nothing else.",
    preview: "Leo arrived safely at Maple Grove.",
  },
  {
    id: "calm",
    label: "Calm",
    blurb: "Recommended. Soft state transitions and quiet kiosk taps too.",
    preview: "Leo softly said they're feeling curious.",
  },
  {
    id: "detailed",
    label: "Detailed",
    blurb: "Every gentle update, including caregiver moments and sensor whispers.",
    preview: "Mira shared a small moment about Leo.",
  },
  {
    id: "emergency_only",
    label: "Emergency-only",
    blurb: "Silence, except for a Circle of Calm.",
    preview: "Maple Grove: \u201cAll is well.\u201d \u2014 Sam",
  },
];

function NotificationsTonePanel() {
  const { user, setUser } = useAuth();
  const [tone, setTone] = useState(user?.notification_tone || "calm");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTone(user?.notification_tone || "calm");
  }, [user?.notification_tone]);

  const pick = async (id) => {
    if (id === tone || busy) return;
    setBusy(true);
    setTone(id);
    try {
      const r = await api.patch("/users/me", { notification_tone: id });
      setUser?.(r.data);
      sonnerToast("Tone gently updated.");
    } catch {
      sonnerToast("Couldn't save. We'll try again, quietly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <BellOff size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Notifications</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        A reassurance system, not an alert system.
      </h2>
      <p className="mt-3 text-base text-[#6A6863] font-light max-w-xl">
        Alouvya will never ping, badge, or interrupt. Choose the tone you want to feel
        when small moments unfold. You can change it any time.
      </p>

      <ul
        data-testid="settings-tone-list"
        className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {TONES.map((t) => {
          const active = tone === t.id;
          return (
            <li
              key={t.id}
              data-testid={`settings-tone-row-${t.id}`}
              className="bg-[#FBF9F6]"
            >
              <button
                type="button"
                onClick={() => pick(t.id)}
                data-testid={`settings-tone-pick-${t.id}`}
                aria-pressed={active}
                className={
                  "w-full text-left p-6 flex items-start gap-5 transition-colors " +
                  (active ? "bg-[#F2EFEA]" : "hover:bg-[#F7F4EF]")
                }
              >
                <span
                  className={
                    "mt-1 h-3.5 w-3.5 rounded-full shrink-0 border " +
                    (active
                      ? "bg-[#E6BFA6] border-[#E6BFA6]"
                      : "bg-transparent border-[#9BA995]")
                  }
                  aria-hidden
                />
                <span className="flex-1">
                  <span className="font-serif text-xl text-[#2C2B29]">{t.label}</span>
                  <span className="block text-sm text-[#6A6863] font-light mt-1">
                    {t.blurb}
                  </span>
                  <span
                    data-testid={`settings-tone-preview-${t.id}`}
                    className="block font-serif italic text-[#2C2B29] mt-3 text-[15px]"
                  >
                    {"\u201c" + t.preview + "\u201d"}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="mt-4 text-xs text-[#6A6863] font-light">
        No red badges. No urgent counters. No push notifications. Just a soft thread
        of recent moments on your home page.
      </p>
    </>
  );
}


/** Kiosk approval mode — parent/admin can require staff approval before a
 *  child's whereabouts tap changes the room's view of their state. */
function KioskApprovalSection() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/children")
      .then((r) => setChildren(r.data || []))
      .catch(() => {});
  }, [user]);

  const visible = children.filter((c) => {
    if (user?.role === "parent") return (c.parent_ids || []).includes(user.id);
    if (user?.role === "admin") return !!c.workspace_id;
    return false;
  });

  const toggle = async (c) => {
    setBusy(c.id);
    try {
      const r = await api.patch(`/children/${c.id}/kiosk-approval-required`, {
        required: !c.kiosk_approval_required,
      });
      setChildren((prev) => prev.map((x) => (x.id === c.id ? r.data : x)));
      sonnerToast(
        r.data.kiosk_approval_required
          ? `${r.data.name} now needs a staff confirmation.`
          : `${r.data.name} can share gently on their own.`
      );
    } catch (err) {
      sonnerToast(err.response?.data?.detail || "Couldn't update.");
    } finally {
      setBusy(null);
    }
  };

  if (visible.length === 0) return null;
  return (
    <>
      <div className="flex items-center gap-2">
        <Lock size={14} strokeWidth={1.5} className="text-[#8A9984]" />
        <span className="eyebrow">Kiosk approval mode</span>
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight">
        Who confirms a child's tap?
      </h2>
      <p className="mt-3 text-base text-[#6A6863] font-light max-w-md">
        For older children, leave this off — their tap quietly updates the room.
        For stricter centers, switch it on — every tap is gently confirmed by
        staff first.
      </p>

      <ul
        data-testid="settings-kiosk-approval-list"
        className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {visible.map((c) => (
          <li
            key={c.id}
            data-testid={`settings-kiosk-approval-row-${c.id}`}
            className="bg-[#FBF9F6] p-6 flex items-center justify-between gap-4 flex-wrap"
          >
            <div>
              <div className="font-serif text-lg">{c.name}</div>
              <div
                className="text-xs text-[#6A6863] mt-1"
                data-testid={`settings-kiosk-approval-status-${c.id}`}
              >
                {c.kiosk_approval_required
                  ? "Staff confirms each tap"
                  : "Child's tap updates the room"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggle(c)}
              disabled={busy === c.id}
              data-testid={`settings-kiosk-approval-toggle-${c.id}`}
              aria-pressed={!!c.kiosk_approval_required}
              className={
                "relative inline-flex items-center w-14 h-7 rounded-full transition-colors duration-300 " +
                (c.kiosk_approval_required ? "bg-[#132619]" : "bg-[#E8E2D5]")
              }
            >
              <span
                className="absolute h-6 w-6 bg-white rounded-full shadow transition-transform duration-300"
                style={{
                  transform: c.kiosk_approval_required
                    ? "translateX(28px)"
                    : "translateX(4px)",
                }}
              />
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}


function LanguagePanel() {
  const { t, locale, lang, setLang, languages } = useI18n();
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);

  // On first mount, if user has a server-side language and it differs from local,
  // prefer the server value.
  useEffect(() => {
    if (user?.language && user.language !== lang) {
      setLang(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  async function pick(code) {
    if (code === lang) return;
    setSaving(true);
    setLang(code);
    try {
      const r = await api.patch("/users/me", { language: code });
      if (setUser) setUser(r.data);
      toast.success(t("settings.language.saved"));
    } catch (_e) {
      // Local preference still applied — server sync was best-effort.
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 text-[#8A9984]">
        <Globe size={14} strokeWidth={1.5} />
        <span className="eyebrow">{t("settings.language.title")}</span>
      </div>
      <p className="mt-3 text-sm text-[#525252]">{t("settings.language.subtitle")}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {languages.map((l) => (
          <button
            type="button"
            key={l.code}
            data-testid={`settings-language-${l.code}`}
            onClick={() => pick(l.code)}
            disabled={saving}
            aria-current={locale === l.code ? "true" : undefined}
            aria-pressed={locale === l.code}
            className={
              "px-4 py-2 rounded-full text-sm transition-colors duration-300 border " +
              (locale === l.code
                ? "bg-[#132619] text-white border-[#1A1B2E]"
                : "bg-white text-[#171717] border-[#171717]/15 hover:bg-[#F2EFEA]/70") +
              (saving ? " opacity-60 cursor-not-allowed" : "")
            }
          >
            {l.native}
          </button>
        ))}
      </div>
    </>
  );
}
